import copy from "copy-to-clipboard";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { HeyLightProductDTO } from "../../HeyLight/useHeyLightProductById.ts";
import { DropDown } from "@normasteaching/norma-ui";
import { Ellipsis } from "lucide-react";
import { HeyLightCheckoutScreenPath } from "../../HeyLight/HeyLightCheckoutScreen.tsx";
import { SuccessMessage } from "../../UI/Success/SuccessMessage.tsx";
import { useMessageController } from "../../UI/useMessageController.ts";

const columnHelper = createColumnHelper<HeyLightProductDTO>();
const columns = [
  columnHelper.accessor("id", {
    header: "ID",
    cell: function IdColumn(info) {
      const { showMessage, message, open } = useMessageController();
      return (
        <>
          <SuccessMessage open={open} message={message} />
          <span
            className={"cursor-pointer"}
            onClick={() => {
              copy(info.getValue());
              showMessage({
                message: "ID del prodotto copiato negli appunti!",
                timeoutMs: 4 * 1000,
              });
            }}
          >
            {info.getValue()}
          </span>
        </>
      );
    },
    maxSize: 100,
  }),
  columnHelper.accessor("internalProductName", {
    header: "Internal Name",
    cell: (info) => info.getValue(),
    maxSize: 100,
  }),
  columnHelper.accessor("priceInCents", {
    header: "Prezzo",
    cell: (info) => {
      const v = info.getValue() ?? 0;
      return `${v.amount / 100} ${v.currency}`;
    },
    maxSize: 100,
  }),
  columnHelper.display({
    id: "actions",
    cell: function Actions(props) {
      const { showMessage, open, message } = useMessageController();
      function createProductUrl(): string {
        const url = new URL(HeyLightCheckoutScreenPath, window.location.origin);
        url.searchParams.set("productId", props.row.original.id);
        return url.toString();
      }
      return (
        <>
          <SuccessMessage open={open} message={message} />
          <DropDown activator={<Ellipsis className={"w-5"} />}>
            <div className={""}>
              <div
                className={
                  "p-3 hover:bg-primary hover:text-white cursor-pointer"
                }
                onClick={() => {
                  copy(createProductUrl());
                  showMessage({
                    message: "URL del prodotto copiato negli appunti!",
                    timeoutMs: 4 * 1000,
                  });
                }}
              >
                <span>Copia URL</span>
              </div>
              <div
                className={
                  "p-3 hover:bg-primary hover:text-white cursor-pointer"
                }
                onClick={() => {
                  window.open(
                    createProductUrl(),
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
              >
                <span>Vedi Checkout</span>
              </div>
            </div>
          </DropDown>
        </>
      );
    },
  }),
];
export function AdminHeyLightProductTable({
  products,
}: {
  products: HeyLightProductDTO[];
}) {
  const table = useReactTable({
    data: products,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <table
      className={
        "w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400"
      }
    >
      <thead
        className={
          "text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400"
        }
      >
        {table.getHeaderGroups().map((headerGroup) => (
          <tr className={""} key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th className={"px-6 py-4"} key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr
            className={
              "bg-white border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200"
            }
            key={row.id}
          >
            {row.getVisibleCells().map((cell) => (
              <td
                style={{
                  maxWidth: cell.column.columnDef.maxSize
                    ? cell.column.columnDef.maxSize
                    : "none",
                  overflow: cell.column.id !== "actions" ? "hidden" : undefined,
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                className={"px-6 py-4"}
                key={cell.id}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      <tfoot>
        {table.getFooterGroups().map((footerGroup) => (
          <tr key={footerGroup.id}>
            {footerGroup.headers.map((header) => (
              <th key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.footer,
                      header.getContext(),
                    )}
              </th>
            ))}
          </tr>
        ))}
      </tfoot>
    </table>
  );
}
